import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ExpenseForm from '../components/ExpenseForm';
import PeriodInput, { periodToStartIso, periodToEndIso, isoToPeriod } from '../components/PeriodInput';
import PageSuggestion from '../components/PageSuggestion';
import { expenseApi, expenseCategoryApi } from '../services/api';
import { Expense, ExpenseCategory, CreateExpenseRequest, UpdateExpenseRequest } from '../types';
import { formatDate } from '../utils/dateUtils';

const formatBRL = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((cents || 0) / 100);

const Expenses: React.FC = () => {
    const defaultPeriod = useMemo(() => isoToPeriod(null), []);
    const [filterStartPeriod, setFilterStartPeriod] = useState(defaultPeriod);
    const [filterEndPeriod, setFilterEndPeriod] = useState(defaultPeriod);
    const [filterCategoryId, setFilterCategoryId] = useState<string>('');

    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [items, setItems] = useState<Expense[]>([]);
    const [filtered, setFiltered] = useState<Expense[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<Expense | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
    const [deleting, setDeleting] = useState(false);

    const itemsPerPage = 10;

    const categoryMap = useMemo(() => {
        const m: Record<string, string> = {};
        categories.forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [categories]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await expenseCategoryApi.getAll();
            setCategories(res.data || []);
        } catch {
            setCategories([]);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const params: { categoryId?: string; dateStart: string; dateEnd: string } = {
                dateStart: startIso,
                dateEnd: endIso,
            };
            if (filterCategoryId) params.categoryId = filterCategoryId;
            const res = await expenseApi.getAll(params);
            setItems(res.data || []);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Não foi possível carregar os gastos.';
            setLoadError(msg);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const f = items.filter(
            (i) =>
                (i.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (categoryMap[i.categoryId] || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFiltered(f);
        setCurrentPage(1);
    }, [searchTerm, items, categoryMap]);

    const handleCreate = async (data: CreateExpenseRequest) => {
        try {
            await expenseApi.create(data);
            await fetchItems();
            setIsModalOpen(false);
            setLoadError(null);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Falha ao registrar gasto.';
            setLoadError(msg);
        }
    };

    const handleUpdate = async (data: UpdateExpenseRequest) => {
        if (!editing) return;
        try {
            await expenseApi.update(editing.id, data);
            await fetchItems();
            setIsModalOpen(false);
            setEditing(null);
            setLoadError(null);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Falha ao atualizar gasto.';
            setLoadError(msg);
        }
    };

    const handleExpenseSubmit = async (data: CreateExpenseRequest | UpdateExpenseRequest) => {
        if (editing) {
            await handleUpdate(data as UpdateExpenseRequest);
        } else {
            await handleCreate(data as CreateExpenseRequest);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await expenseApi.delete(deleteTarget.id);
            setDeleteTarget(null);
            await fetchItems();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Falha ao excluir gasto.';
            setLoadError(msg);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            key: 'date',
            label: 'Data',
            render: (value: string) => formatDate(value),
        },
        {
            key: 'categoryId',
            label: 'Categoria',
            render: (value: string) => categoryMap[value] || '—',
        },
        {
            key: 'amount',
            label: 'Valor',
            render: (value: number) => formatBRL(value),
        },
        { key: 'description', label: 'Descrição', render: (v: string) => v || '—' },
    ];

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

    if (loading && items.length === 0 && !loadError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {loadError && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{loadError}</div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">Gastos</h1>
                    <p className="text-sm sm:text-base text-secondary-600">Registre e acompanhe seus gastos por categoria</p>
                    <PageSuggestion>
                        Use esta tela para lançar despesas avulsas do dia a dia e manter o relatório mensal sempre atualizado.
                    </PageSuggestion>
                </div>
                <button
                    onClick={() => { setEditing(null); setIsModalOpen(true); }}
                    className="btn-primary w-full sm:w-auto shrink-0 flex items-center justify-center"
                    disabled={categories.length === 0}
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo gasto
                </button>
            </div>

            <div className="bg-white p-3 sm:p-4 rounded-lg border border-secondary-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[auto_auto_auto_auto] gap-3 items-end">
                    <div className="w-full sm:min-w-[120px]">
                        <PeriodInput
                            label="Data inicial (MM/AAAA)"
                            name="dateStart"
                            value={filterStartPeriod}
                            onChange={(v) => {
                                setFilterStartPeriod(v);
                                setFilterEndPeriod(v);
                            }}
                            compact
                        />
                    </div>
                    <div className="w-full sm:min-w-[120px]">
                        <PeriodInput label="Data final (MM/AAAA)" name="dateEnd" value={filterEndPeriod} onChange={setFilterEndPeriod} compact />
                    </div>
                    <div className="w-full sm:min-w-[140px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Categoria</label>
                        <select
                            className="input-field"
                            value={filterCategoryId}
                            onChange={(e) => setFilterCategoryId(e.target.value)}
                        >
                            <option value="">Todas</option>
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <button type="button" onClick={fetchItems} className="btn-primary w-full sm:w-auto shrink-0 h-[42px]">
                        Filtrar
                    </button>
                </div>
            </div>

            {categories.length === 0 && (
                <p className="text-secondary-600 text-sm">
                    Cadastre ao menos uma categoria em &quot;Categorias de gastos&quot; para registrar gastos.
                </p>
            )}

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por descrição ou categoria..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={(row) => { setEditing(row); setIsModalOpen(true); }}
                editButtonText="Editar"
                onDelete={(row) => setDeleteTarget(row)}
                deleteButtonText="Excluir"
                mobileCardRenderer={(item) => (
                    <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-medium text-secondary-900 truncate">
                                    {categoryMap[item.categoryId] || '—'}
                                </p>
                                <p className="text-xs text-secondary-600 mt-0.5">{formatDate(item.date)}</p>
                            </div>
                            <p className="text-base font-semibold text-primary-700 shrink-0">
                                {formatBRL(item.amount)}
                            </p>
                        </div>
                        {item.description && (
                            <p className="text-sm text-secondary-600 break-words">{item.description}</p>
                        )}
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => { setEditing(item); setIsModalOpen(true); }}
                                className="btn-primary flex-1 text-sm py-2"
                            >
                                Editar
                            </button>
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(item)}
                                className="btn-secondary flex-1 text-sm py-2"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                )}
            />

            <Modal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                title={editing ? 'Editar gasto' : 'Novo gasto'}
            >
                <ExpenseForm
                    expense={editing}
                    categories={categories}
                    onSubmit={handleExpenseSubmit}
                    onCancel={() => { setIsModalOpen(false); setEditing(null); }}
                />
            </Modal>

            <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Excluir gasto">
                <p className="text-secondary-700 mb-4">
                    Excluir este gasto de {deleteTarget ? formatBRL(deleteTarget.amount) : ''}?
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button type="button" className="btn-secondary w-full sm:w-auto" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancelar
                    </button>
                    <button type="button" className="btn-primary w-full sm:w-auto bg-red-600 hover:bg-red-700" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Excluindo...' : 'Excluir'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default Expenses;
